import React, {useEffect, useMemo, useRef, useState} from "react"
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    IconButton,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import VisibilityIcon from "@mui/icons-material/Visibility"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"
import {toast} from "react-toastify"
import Content from "../../../../anwendungskonfigurator/components/Content"
import {
    getMerkmal,
    getAntworttyp,
} from "../../../../anwendungskonfigurator/misc/widget-functions"

const UnfallDynamicColumnTableContainer = ({
    container,
    value,
    onValueChange,
    getDataFromPointer,
    updateData,
    triggerAction,
    disabled,
}) => {
    const {children = [], action} = container
    const columns = value ?? []

    const sparte = getDataFromPointer("/anbahnung/sparte")
    const spartenString = sparte === "UNFALL_GRUPPE" ? "Gruppe" : "Person"

    const hiddenPointerFor = (columnIndex) =>
        container.subtext?.replace("[i]", columnIndex)
    const isGroupHidden = (columnIndex) =>
        getDataFromPointer(hiddenPointerFor(columnIndex)) === true
    const toggleAllGroupsHidden = () => {
        const nextHidden = !columns.every((_, index) => isGroupHidden(index))
        columns.forEach((_, index) => {
            updateData(nextHidden, hiddenPointerFor(index))
        })
    }

    const groupNameWidget = children?.[0]
    const fieldWidgets = children?.slice(1) ?? []

    const groupNamePointer = getMerkmal(groupNameWidget)?.pointer
    const groupNameKey =
        groupNamePointer?.split("/").findLast(Boolean) ?? "label"

    const [editIndex, setEditIndex] = useState(null)
    const [isNewGroup, setIsNewGroup] = useState(false)
    const [originalName, setOriginalName] = useState(undefined)
    const COLUMNSPERPAGE = Number(container.perPage) || 3

    const page =
        (container.pagePointer
            ? getDataFromPointer(container.pagePointer)
            : 0) ?? 0
    const setPage = (newPage) => {
        if (container.pagePointer) updateData(newPage, container.pagePointer)
    }

    const prevColumnCount = useRef(columns.length)
    useEffect(() => {
        const previousCount = prevColumnCount.current
        prevColumnCount.current = columns.length

        if (columns.length !== previousCount + 1) return

        const newIndex = columns.length - 1
        const newGroupName = columns[newIndex]?.[groupNameKey]
        if (newGroupName?.toString().trim()) return

        setEditIndex(newIndex)
        setIsNewGroup(true)
        setOriginalName(undefined)
    }, [columns.length, groupNameKey])

    const handleOpenEditDialog = (index) => {
        setEditIndex(index)
        setIsNewGroup(false)
        setOriginalName(columns[index]?.[groupNameKey])
    }

    const closeDialog = () => {
        setEditIndex(null)
        setIsNewGroup(false)
        setOriginalName(undefined)
    }

    const handleConfirm = () => {
        const name = columns[editIndex]?.[groupNameKey]
        if (!name || !name.trim()) return

        const isDuplicate = columns.some(
            (col, index) => index !== editIndex && col[groupNameKey] === name,
        )
        if (isDuplicate) {
            toast.error(
                `Es existiert schon eine ${spartenString} mit diesem Namen!`,
                {toastId: "unfall-gruppe-exists"},
            )
            return
        }

        action && triggerAction(action)
        closeDialog()
    }

    const elementEditName =
        sparte === "UNFALL_GRUPPE"
            ? "Gruppenname bearbeiten"
            : "Personenname bearbeiten"
    const elementDeleteLabel =
        sparte === "UNFALL_GRUPPE" ? "Gruppe löschen" : "Person löschen"

    const handleCancel = () => {
        if (isNewGroup) {
            handleDelete(editIndex)
        } else {
            onValueChange(
                columns.map((col, index) =>
                    index === editIndex
                        ? {...col, [groupNameKey]: originalName}
                        : col,
                ),
            )
        }
        closeDialog()
    }

    const handleDelete = (index) => {
        const next = columns.filter((_, i) => i !== index)
        onValueChange(next)
        const lastPage = Math.max(
            0,
            Math.ceil(next.length / COLUMNSPERPAGE) - 1,
        )
        if (page > lastPage) setPage(lastPage)
        action && triggerAction(action)
    }

    const startIndex = page * COLUMNSPERPAGE
    const visibleColumns = columns.slice(startIndex, startIndex + COLUMNSPERPAGE)

    const templateCache = useMemo(() => new Map(), [container])

    const indexNode = (node, columnIndex) => {
        const walk = (obj) => {
            if (Array.isArray(obj)) return obj.map(walk)
            if (obj && typeof obj === "object") {
                const next = {}
                for (const [key, val] of Object.entries(obj)) {
                    if (typeof val === "string") {
                        next[key] = val.split("[i]").join(String(columnIndex))
                    } else {
                        next[key] = walk(val)
                    }
                }
                if (typeof next.type === "string" && typeof next.id === "string") {
                    next.id = `${next.id}-${columnIndex}`
                }
                return next
            }
            return obj
        }
        return walk(node)
    }

    const getNodeTemplate = (node, columnIndex) => {
        const cacheKey = node.id + "-" + columnIndex
        const cached = templateCache.get(cacheKey)
        if (cached) return cached

        const renderNode = indexNode(node, columnIndex)
        templateCache.set(cacheKey, renderNode)
        return renderNode
    }

    const renderNodeForColumn = (node, columnIndex, type) => {
        if (!node) return null

        const renderNode = getNodeTemplate(node, columnIndex)

        return (
            <Content
                key={renderNode.id}
                child={renderNode}
                type={type}
                disabled={disabled}
            />
        )
    }

    return (
        <Box>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `200px repeat(${visibleColumns?.length}, 1fr)`,
                    alignItems: "center",
                    border: "1px solid #eee",
                    borderRadius: "4px",
                    overflow: "hidden",
                }}
            >
                <Box bgcolor="#f8f9fa" padding={2} height="100%">
                    <Typography fontWeight={600}></Typography>
                </Box>

                {visibleColumns?.map((col, localIndex) => {
                    const absoluteIndex = startIndex + localIndex
                    return (
                        <Box
                            key={"header-" + absoluteIndex}
                            bgcolor="#f8f9fa"
                            padding={2}
                            height="100%"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                            }}
                        >
                            <Typography fontWeight={600}>
                                {col[groupNameKey]}
                            </Typography>
                            <Box sx={{display: "flex", alignItems: "center"}}>
                                <IconButton
                                    size="small"
                                    onClick={toggleAllGroupsHidden}
                                >
                                    {isGroupHidden(absoluteIndex) ? (
                                        <VisibilityOffIcon fontSize="small" />
                                    ) : (
                                        <VisibilityIcon fontSize="small" />
                                    )}
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label={elementEditName}
                                    onClick={() =>
                                        handleOpenEditDialog(absoluteIndex)
                                    }
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label={elementDeleteLabel}
                                    onClick={() => handleDelete(absoluteIndex)}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    )
                })}

                {columns.length > 0 &&
                    fieldWidgets?.map((widget) => (
                        <React.Fragment key={widget.id}>
                            <Box
                                pl={2}
                                sx={{
                                    borderBottom: "1px solid #eee",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                                height={"100%"}
                            >
                                <Typography>{widget.label ?? ""}</Typography>
                            </Box>
                            {visibleColumns?.map((col, localIndex) => {
                                const absoluteIndex = startIndex + localIndex
                                return (
                                    <Box
                                        key={widget.id + "-" + absoluteIndex}
                                        padding={2}
                                        height={"100%"}
                                        sx={{borderBottom: "1px solid #eee"}}
                                    >
                                        {renderNodeForColumn(
                                            widget,
                                            absoluteIndex,
                                        )}
                                    </Box>
                                )
                            })}
                        </React.Fragment>
                    ))}
            </Box>

            {columns.length === 0 && (
                <Box padding={2} textAlign="center">
                    <Typography variant="body1">
                        Keine {spartenString} vorhanden
                    </Typography>
                </Box>
            )}

            <Dialog open={editIndex !== null} onClose={handleCancel}>
                <DialogTitle>
                    {isNewGroup
                        ? `Neue ${spartenString} hinzufügen`
                        : `${spartenString} bearbeiten`}
                </DialogTitle>
                <DialogContent>
                    {editIndex !== null &&
                        renderNodeForColumn(
                            groupNameWidget,
                            editIndex,
                            getAntworttyp(groupNameWidget),
                        )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>Abbrechen</Button>
                    <Button onClick={handleConfirm} variant="contained">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default UnfallDynamicColumnTableContainer
